/**
 * The support-call engine, shared by all three SPAs.
 *
 * Transport-agnostic on purpose: the buyer/admin apps speak `@most/api-client`, the
 * provider app has its own fetch seam, so this hook takes a tiny `CallApi` adapter and
 * never imports either.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  SIGNALING MODEL (matches the backend's ANON-W rule)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WebSocket frames carry NO payload — `call.state` / `call.signal` are nudges holding only
 * the call id. SDP offers/answers and ICE candidates travel as opaque strings through
 * POST/GET /chat/calls/{id}/signals. The hook additionally POLLS while a call is live, so
 * a dropped frame degrades to latency, never to a dead call.
 *
 * Offer direction: the INITIATOR creates the offer once the peer accepts. The callee's
 * `accept()` resolves before any SDP exists; media flows a moment later.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type CallKind = 'audio' | 'video';
export type CallDTOState = 'ringing' | 'active' | 'ended' | 'declined' | 'missed';

export interface ChatCallDTO {
  id: string;
  conversation_id: string;
  kind: CallKind;
  state: CallDTOState;
  initiated_by: 'user' | 'admin';
  created_at: string;
  accepted_at: string | null;
}

/** The five REST verbs the engine needs, in the host app's own transport. */
export interface CallApi {
  start(kind: CallKind, conversationId?: string): Promise<ChatCallDTO>;
  get(id: string): Promise<ChatCallDTO>;
  accept(id: string): Promise<ChatCallDTO>;
  decline(id: string): Promise<ChatCallDTO>;
  end(id: string): Promise<ChatCallDTO>;
  postSignal(id: string, data: string): Promise<void>;
  getSignals(id: string): Promise<string[]>;
}

/** What the UI renders. `incoming`/`outgoing` are both "ringing", seen from each end. */
export type CallPhase = 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'active' | 'ended';

export interface UseSupportCallResult {
  phase: CallPhase;
  call: ChatCallDTO | null;
  /** Set once local media is captured; attach to a muted <video>/<audio> element. */
  localStream: MediaStream | null;
  /** Set once the peer's tracks arrive. */
  remoteStream: MediaStream | null;
  error: string | null;

  startCall(kind: CallKind, conversationId?: string): Promise<void>;
  accept(): Promise<void>;
  decline(): Promise<void>;
  hangUp(): Promise<void>;

  /** Feed `call.state` / `call.signal` WS frames here (the frame's id). */
  onCallStateFrame(callId: string): void;
  onCallSignalFrame(callId: string): void;
}

interface UseSupportCallOptions {
  api: CallApi;
  /** Which side of every call this app is. */
  side: 'user' | 'admin';
  /** Fired whenever a call reaches a terminal state (refresh the thread — a log line landed). */
  onTerminal?: () => void;
  /** Extra ICE servers (TURN) if the deployment ever needs one. */
  iceServers?: RTCIceServer[];
}

const DEFAULT_ICE: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

/** How often we drain signals / poll state while a call is establishing or live. */
const SIGNAL_POLL_MS = 1500;
const STATE_POLL_MS = 2000;

type Signal =
  | { t: 'offer' | 'answer'; sdp: string }
  | { t: 'ice'; candidate: RTCIceCandidateInit };

export function useSupportCall({ api, side, onTerminal, iceServers }: UseSupportCallOptions): UseSupportCallResult {
  const [phase, setPhase] = useState<CallPhase>('idle');
  const [call, setCall] = useState<ChatCallDTO | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const callRef = useRef<ChatCallDTO | null>(null);
  const phaseRef = useRef<CallPhase>('idle');
  const offerSentRef = useRef(false);
  const pendingIceRef = useRef<RTCIceCandidateInit[]>([]);

  const setPhaseBoth = useCallback((p: CallPhase) => {
    phaseRef.current = p;
    setPhase(p);
  }, []);
  const setCallBoth = useCallback((c: ChatCallDTO | null) => {
    callRef.current = c;
    setCall(c);
  }, []);

  const cleanupMedia = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;
    offerSentRef.current = false;
    pendingIceRef.current = [];
    setLocalStream((s) => {
      s?.getTracks().forEach((t) => t.stop());
      return null;
    });
    setRemoteStream(null);
  }, []);

  const finish = useCallback(() => {
    cleanupMedia();
    setPhaseBoth('ended');
    onTerminal?.();
    // Fall back to idle after a beat so the UI can flash "call ended".
    window.setTimeout(() => {
      if (phaseRef.current === 'ended') {
        setPhaseBoth('idle');
        setCallBoth(null);
      }
    }, 2500);
  }, [cleanupMedia, onTerminal, setCallBoth, setPhaseBoth]);

  const captureMedia = useCallback(async (kind: CallKind): Promise<MediaStream> => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: kind === 'video',
    });
    setLocalStream(stream);
    return stream;
  }, []);

  const buildPeer = useCallback(
    (current: ChatCallDTO, stream: MediaStream) => {
      const pc = new RTCPeerConnection({ iceServers: iceServers ?? DEFAULT_ICE });
      pcRef.current = pc;

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const remote = new MediaStream();
      setRemoteStream(remote);
      pc.ontrack = (ev) => {
        ev.streams[0]?.getTracks().forEach((t) => remote.addTrack(t));
        setPhaseBoth('active');
      };

      pc.onicecandidate = (ev) => {
        if (ev.candidate) {
          const sig: Signal = { t: 'ice', candidate: ev.candidate.toJSON() };
          void api.postSignal(current.id, JSON.stringify(sig)).catch(() => undefined);
        }
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          setError('connection_lost');
        }
      };

      return pc;
    },
    [api, iceServers, setPhaseBoth],
  );

  /** The initiator's offer, sent exactly once, after the peer accepts. */
  const sendOfferOnce = useCallback(
    async (current: ChatCallDTO) => {
      if (offerSentRef.current) return;
      offerSentRef.current = true;

      const stream = await captureMedia(current.kind);
      const pc = buildPeer(current, stream);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const sig: Signal = { t: 'offer', sdp: offer.sdp ?? '' };
      await api.postSignal(current.id, JSON.stringify(sig));
    },
    [api, buildPeer, captureMedia],
  );

  const applySignal = useCallback(
    async (raw: string) => {
      const current = callRef.current;
      if (!current) return;
      let sig: Signal;
      try {
        sig = JSON.parse(raw) as Signal;
      } catch {
        return; // a malformed blob is the peer's bug, not a reason to crash the call UI
      }

      if (sig.t === 'offer') {
        // Callee path: media was captured in accept(); the peer connection starts here.
        const stream =
          localStream ?? (await captureMedia(current.kind));
        const pc = pcRef.current ?? buildPeer(current, stream);
        await pc.setRemoteDescription({ type: 'offer', sdp: sig.sdp });
        for (const c of pendingIceRef.current) await pc.addIceCandidate(c).catch(() => undefined);
        pendingIceRef.current = [];
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        const out: Signal = { t: 'answer', sdp: answer.sdp ?? '' };
        await api.postSignal(current.id, JSON.stringify(out));
        return;
      }

      if (sig.t === 'answer') {
        await pcRef.current?.setRemoteDescription({ type: 'answer', sdp: sig.sdp });
        return;
      }

      if (sig.t !== 'ice') return;

      // ICE before the remote description exists must wait, not be dropped.
      const pc = pcRef.current;
      if (pc && pc.remoteDescription) {
        await pc.addIceCandidate(sig.candidate).catch(() => undefined);
      } else {
        pendingIceRef.current.push(sig.candidate);
      }
    },
    [api, buildPeer, captureMedia, localStream],
  );

  const drainSignals = useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    let signals: string[];
    try {
      signals = await api.getSignals(current.id);
    } catch {
      return;
    }
    for (const raw of signals) {
      await applySignal(raw);
    }
  }, [api, applySignal]);

  const refreshState = useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    let latest: ChatCallDTO;
    try {
      latest = await api.get(current.id);
    } catch {
      // 404 = expired in Redis = the call is long over.
      finish();
      return;
    }
    setCallBoth(latest);

    if (latest.state === 'active') {
      if (latest.initiated_by === side) {
        void sendOfferOnce(latest);
      }
      if (phaseRef.current === 'outgoing' || phaseRef.current === 'incoming') {
        setPhaseBoth('connecting');
      }
    } else if (latest.state !== 'ringing') {
      finish();
    }
  }, [api, finish, sendOfferOnce, setCallBoth, setPhaseBoth, side]);

  // Poll while a call is anywhere between ringing and hung up. WS frames arrive too and
  // just make this faster; correctness never depends on the socket.
  useEffect(() => {
    if (phase === 'idle' || phase === 'ended') return;
    const stateTimer = window.setInterval(() => void refreshState(), STATE_POLL_MS);
    const signalTimer = window.setInterval(() => void drainSignals(), SIGNAL_POLL_MS);
    return () => {
      window.clearInterval(stateTimer);
      window.clearInterval(signalTimer);
    };
  }, [phase, refreshState, drainSignals]);

  const startCall = useCallback(
    async (kind: CallKind, conversationId?: string) => {
      if (phaseRef.current !== 'idle') return;
      setError(null);
      try {
        // Capture BEFORE dialing: if the user denies the mic, nothing rang anywhere.
        await captureMedia(kind);
        const created = await api.start(kind, conversationId);
        setCallBoth(created);
        setPhaseBoth('outgoing');
      } catch (e) {
        cleanupMedia();
        setError(e instanceof DOMException ? 'media_denied' : 'start_failed');
      }
    },
    [api, captureMedia, cleanupMedia, setCallBoth, setPhaseBoth],
  );

  const accept = useCallback(async () => {
    const current = callRef.current;
    if (!current || phaseRef.current !== 'incoming') return;
    setError(null);
    try {
      await captureMedia(current.kind);
      const latest = await api.accept(current.id);
      setCallBoth(latest);
      setPhaseBoth('connecting');
      // The initiator now sends the offer; our polling picks it up.
    } catch (e) {
      cleanupMedia();
      setError(e instanceof DOMException ? 'media_denied' : 'accept_failed');
    }
  }, [api, captureMedia, cleanupMedia, setCallBoth, setPhaseBoth]);

  const decline = useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    try {
      await api.decline(current.id);
    } catch {
      // Already terminal on the server — the finish below is still right.
    }
    finish();
  }, [api, finish]);

  const hangUp = useCallback(async () => {
    const current = callRef.current;
    if (!current) {
      cleanupMedia();
      setPhaseBoth('idle');
      return;
    }
    try {
      await api.end(current.id);
    } catch {
      // ditto
    }
    finish();
  }, [api, cleanupMedia, finish, setPhaseBoth]);

  /** WS `call.state` frame: someone is calling us, or our call changed state. */
  const onCallStateFrame = useCallback(
    (callId: string) => {
      const current = callRef.current;
      if (current && current.id === callId) {
        void refreshState();
        return;
      }
      if (phaseRef.current !== 'idle') return; // busy — ignore a second incoming call
      void (async () => {
        let incoming: ChatCallDTO;
        try {
          incoming = await api.get(callId);
        } catch {
          return;
        }
        // Only a ringing call from the OTHER side is an incoming call.
        if (incoming.state === 'ringing' && incoming.initiated_by !== side) {
          setCallBoth(incoming);
          setPhaseBoth('incoming');
        }
      })();
    },
    [api, refreshState, setCallBoth, setPhaseBoth, side],
  );

  const onCallSignalFrame = useCallback(
    (callId: string) => {
      if (callRef.current?.id === callId) void drainSignals();
    },
    [drainSignals],
  );

  // Leaving the page mid-call: hang up best-effort so the peer is not left ringing.
  useEffect(() => {
    const onUnload = () => {
      const current = callRef.current;
      if (current && phaseRef.current !== 'idle' && phaseRef.current !== 'ended') {
        // sendBeacon cannot carry cookies-with-CSRF-origin reliably across stacks; the
        // Redis TTL is the real backstop. This is only a courtesy attempt.
        void api.end(current.id).catch(() => undefined);
      }
    };
    window.addEventListener('pagehide', onUnload);
    return () => window.removeEventListener('pagehide', onUnload);
  }, [api]);

  return {
    phase,
    call,
    localStream,
    remoteStream,
    error,
    startCall,
    accept,
    decline,
    hangUp,
    onCallStateFrame,
    onCallSignalFrame,
  };
}
